import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input, toast } from '@/shared/ui';
import { DateField, TextField } from '@/shared/forms';
import { vehicleDocumentTypeSelector } from '../../../components/vehicleCodebooks';
import { fileToBase64, MAX_ATTACHMENT_SIZE } from '../../internal/attachments';
import type { VehicleDocumentFileValue } from '../vehicleDocumentFormSchema';
import { type VehicleDocumentFormValues } from '../vehicleDocumentFormSchema';
import { useVehicleDocumentForm } from '../useVehicleDocumentForm';

const DocumentTypeField = vehicleDocumentTypeSelector.Field;

interface VehicleDocumentDialogProps {
  vehicleId: number;
  initialValues: VehicleDocumentFormValues;
  onClose: () => void;
}

/**
 * Dialog metadat dokumentu a jeho příloh. Vybraný soubor se neuploaduje hned — jde do stavu
 * formuláře a odešle se až s `Uložit`, aby dokument i přílohy vznikly jednou transakcí.
 */
export function VehicleDocumentDialog({
  vehicleId,
  initialValues,
  onClose,
}: VehicleDocumentDialogProps) {
  const { t } = useTranslation('inventory');
  const [fileError, setFileError] = useState<string | null>(null);
  const isEdit = initialValues.id != null;

  const { form, submit, isSubmitting } = useVehicleDocumentForm({
    vehicleId,
    initialValues,
    onSaved: () => {
      onClose();
      toast({ severity: 'success', description: t('documents.toast.saved') });
    },
    onError: () =>
      toast({
        severity: 'error',
        title: t('documents.toast.errorTitle'),
        description: t('documents.toast.saveError'),
      }),
  });

  const addFiles = async (fileList: FileList | null) => {
    if (!fileList) return;
    const selected = Array.from(fileList);
    if (selected.some((file) => file.size > MAX_ATTACHMENT_SIZE)) {
      setFileError(t('documents.validation.fileTooLarge'));
      return;
    }

    const added: VehicleDocumentFileValue[] = await Promise.all(
      selected.map(async (file) => ({
        id: null,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: await fileToBase64(file),
      }))
    );

    setFileError(null);
    form.setFieldValue('files', (current) => [...current, ...added]);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? t('documents.dialog.editTitle') : t('documents.dialog.addTitle')}
      icon="doc"
      width="lg"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('documents.cancel')}
          </Button>
          <form.Subscribe selector={(state) => state.canSubmit}>
            {(canSubmit) => (
              <Button
                type="button"
                variant="primary"
                loading={isSubmitting}
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {t('documents.save')}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <div className="gap-phx-4 flex flex-col">
        <div className="gap-phx-4 grid grid-cols-1 md:grid-cols-2">
          <form.Field name="documentType">
            {(field) => (
              <DocumentTypeField
                field={field}
                mode="single"
                label={t('documents.cols.type')}
                required
                testId="document-type"
              />
            )}
          </form.Field>

          <form.Field name="number">
            {(field) => (
              <TextField field={field} label={t('documents.cols.number')} required maxLength={50} />
            )}
          </form.Field>

          <form.Field name="issuedOn">
            {(field) => <DateField field={field} label={t('documents.cols.issuedOn')} />}
          </form.Field>

          <form.Field name="validTo">
            {(field) => <DateField field={field} label={t('documents.cols.validTo')} required />}
          </form.Field>
        </div>

        <Field
          label={t('documents.files.label')}
          hint={t('documents.files.hint')}
          error={fileError ?? undefined}
        >
          <Input type="file" multiple onChange={(event) => void addFiles(event.target.files)} />
        </Field>

        <form.Field name="files" mode="array">
          {(filesField) =>
            filesField.state.value.length === 0 ? null : (
              <div className="gap-phx-2 flex flex-col">
                {filesField.state.value.map((file, index) => (
                  <div
                    key={file.id ?? `${file.fileName}-${index}`}
                    className="border-phx-border px-phx-3 py-phx-2 rounded-phx flex items-center justify-between border"
                  >
                    {file.id ? (
                      <a
                        className="text-phx-link text-phx-sm hover:underline"
                        href={`/api/inventory/vehicles/${vehicleId}/documents/${initialValues.id}/files/${file.id}`}
                      >
                        {file.fileName}
                      </a>
                    ) : (
                      <span className="text-phx-sm">{file.fileName}</span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      iconLeft="trash"
                      aria-label={t('documents.files.remove')}
                      onClick={() => filesField.removeValue(index)}
                    />
                  </div>
                ))}
              </div>
            )
          }
        </form.Field>
      </div>
    </Dialog>
  );
}
